import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { Organization, OrganizationMember, UserRole } from '../../types/organization.types';

/**
 * Firebase Organizations Service
 * Handles organization CRUD operations and member management
 */
export const organizationsService = {
  /**
   * Create a new organization
   * Called during signup to create the user's organization
   */
  async createOrganization(name: string, ownerId: string): Promise<string> {
    try {
      const orgRef = doc(collection(db, 'organizations'));
      const orgId = orgRef.id;

      await setDoc(orgRef, {
        name,
        ownerId,
        settings: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          dateFormat: 'DD-MM-YYYY',
          weekStartsOn: 'monday',
          availabilityMode: 'daily',
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return orgId;
    } catch (error) {
      console.error('Create organization error:', error);
      throw error;
    }
  },

  /**
   * Get organization by ID
   */
  async getOrganization(orgId: string): Promise<Organization | null> {
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (orgDoc.exists()) {
        return {
          id: orgDoc.id,
          ...orgDoc.data(),
        } as Organization;
      }
      return null;
    } catch (error) {
      console.error('Get organization error:', error);
      throw error;
    }
  },

  /**
   * Update organization details
   * Only owners can update organization settings
   */
  async updateOrganization(
    orgId: string,
    data: Partial<Omit<Organization, 'id' | 'ownerId' | 'createdAt' | 'updatedAt'>>
  ): Promise<void> {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      await updateDoc(orgRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Update organization error:', error);
      throw error;
    }
  },

  /**
   * Delete organization
   * Only owners can delete organizations
   * Note: This doesn't delete subcollections (shops, employees, schedules)
   * You might want to use Cloud Functions for complete deletion
   */
  async deleteOrganization(orgId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'organizations', orgId));
    } catch (error) {
      console.error('Delete organization error:', error);
      throw error;
    }
  },

  /**
   * Get all members of an organization
   * Reads from the members subcollection
   */
  async getOrganizationMembers(orgId: string): Promise<OrganizationMember[]> {
    try {
      const membersRef = collection(db, 'organizations', orgId, 'members');
      const querySnapshot = await getDocs(membersRef);

      const members: OrganizationMember[] = [];
      querySnapshot.forEach((doc) => {
        const memberData = doc.data();
        members.push({
          userId: doc.id,
          email: memberData.email || '',
          displayName: memberData.displayName || '',
          role: memberData.role || 'viewer',
          joinedAt: memberData.joinedAt,
        });
      });

      return members;
    } catch (error) {
      console.error('Get organization members error:', error);
      throw error;
    }
  },

  /**
   * Add or update a member in the organization's members subcollection
   * This should be called when a user joins the organization or updates their profile
   */
  async syncMember(
    orgId: string,
    userId: string,
    data: {
      email: string;
      displayName: string;
      role: UserRole;
      joinedAt?: Timestamp;
    }
  ): Promise<void> {
    try {
      const memberRef = doc(db, 'organizations', orgId, 'members', userId);
      const memberData = {
        email: data.email,
        displayName: data.displayName,
        role: data.role,
        joinedAt: data.joinedAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(memberRef, memberData, { merge: true });
    } catch (error) {
      console.error('Sync member error:', error);
      throw error;
    }
  },

  /**
   * Remove a user from organization
   * Sets their organizationId and role to undefined and removes from members subcollection
   */
  async removeMember(orgId: string, userId: string): Promise<void> {
    try {
      // Remove from users collection (clear organization reference)
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        organizationId: null,
        role: null,
        updatedAt: serverTimestamp(),
      });

      // Remove from members subcollection
      const memberRef = doc(db, 'organizations', orgId, 'members', userId);
      await deleteDoc(memberRef);
    } catch (error) {
      console.error('Remove member error:', error);
      throw error;
    }
  },

  /**
   * Update a member's role
   * Only owners and admins can update roles
   */
  async updateMemberRole(
    orgId: string,
    userId: string,
    role: 'owner' | 'admin' | 'manager' | 'viewer'
  ): Promise<void> {
    try {
      // Update in users collection
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role,
        updatedAt: serverTimestamp(),
      });

      // Update in members subcollection
      const memberRef = doc(db, 'organizations', orgId, 'members', userId);
      await updateDoc(memberRef, {
        role,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Update member role error:', error);
      throw error;
    }
  },

  /**
   * Transfer organization ownership to another member
   * Only current owner can transfer ownership
   */
  async transferOwnership(orgId: string, newOwnerId: string, currentOwnerId: string): Promise<void> {
    try {
      // Update organization owner
      const orgRef = doc(db, 'organizations', orgId);
      await updateDoc(orgRef, {
        ownerId: newOwnerId,
        updatedAt: serverTimestamp(),
      });

      // Update new owner's role in users collection
      const newOwnerRef = doc(db, 'users', newOwnerId);
      await updateDoc(newOwnerRef, {
        role: 'owner',
        updatedAt: serverTimestamp(),
      });

      // Update new owner's role in members subcollection
      const newOwnerMemberRef = doc(db, 'organizations', orgId, 'members', newOwnerId);
      await updateDoc(newOwnerMemberRef, {
        role: 'owner',
        updatedAt: serverTimestamp(),
      });

      // Downgrade current owner to admin in users collection
      const currentOwnerRef = doc(db, 'users', currentOwnerId);
      await updateDoc(currentOwnerRef, {
        role: 'admin',
        updatedAt: serverTimestamp(),
      });

      // Downgrade current owner to admin in members subcollection
      const currentOwnerMemberRef = doc(db, 'organizations', orgId, 'members', currentOwnerId);
      await updateDoc(currentOwnerMemberRef, {
        role: 'admin',
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Transfer ownership error:', error);
      throw error;
    }
  },
};
